import React, { useEffect, useState } from "react";

import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { components } from "ComponentRenderer.js";
import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";
import { Youtube, Facebook, Instagram, Linkedin } from "lucide-react";

import logo from "../../images/youngmindslogo.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { ReactComponent as CaretIcon } from "../../images/aurum_Icons/carat.svg";

const Header = styled.header(() => [
  tw`w-full mx-auto flex items-center justify-between bg-white pl-4 lg:px-16 py-0 lg:py-2`,
  css`
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15); /* Custom strong shadow */
    border-radius: 0 0 12px 12px; /* Optional: soft rounded bottom */
    position: relative;
    z-index: 50; /* Keeps it above background */
  `,
]);

const iconStyle = {
  color: "white",
  width: 16,
  height: 16,
  paddingRight: "8px",
};

const TopBar = styled.div`
  width: 100%;
  background-color: #3f6c68;
  margin-top: -1.3rem;
  margin-bottom: 0rem;
  font-size: 14px;
  line-height: 1;
  padding: 4px 0;
  display: flex;
  justify-content: space-around;
  .header-top {
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: 6px 12px; /* ✅ compact padding */
    gap: 10px;
  }
  .main-top-bar {
    display: flex;
    justify-content: space-around;
  }
  .header-top-right {
    display: flex;
    justify-content: flex-end;
    margin-left: auto; /* ✅ Pushes this div to the right end */
  }

  .social-login {
    display: flex;
    gap: 10px;
    justify-content: end;
  }

  .social-login a {
    color: white;
  }

  .header-notify marquee {
    color: white;
    font-weight: 500;
  }

  .header-info {
    list-style: none;
    display: flex;
    align-items: center;
    gap: 10px; /* ✅ uniform spacing */
    margin: 0;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      gap: 6px; /* ✅ tighten icon spacing */

      a {
        color: white;
        text-decoration: none;
        display: flex;
        align-items: center;
        gap: 4px;
        font-weight: 500;
      }
    }

    .social-icon {
      display: flex;
      align-items: center;
      gap: 8px;

      a {
        font-size: 13px; /* ✅ small icons */
        display: flex;
        align-items: center;
      }
    }
  }

  /* ✅ Responsive handling */
  @media (max-width: 768px) {
    font-size: 11px;

    .header-top {
      flex-wrap: wrap; /* Wrap only on mobile */
      justify-content: center;
      gap: 6px;
      text-align: center;
    }

    .header-notify {
      width: 100%;
      order: 1;
    }

    .header-info {
      justify-content: center;
    }
  }
`;

export const NavBar = styled.ul`
  z-index: 99;
  shadow-box: 0 4px 12px rgba(0, 0, 0, 0.1);
  .caret {
    transform: roate(90deg);
  }
`;
//<NavLink href={innerPages.germanlanguageforclass11.url}>for Class 11th</NavLink>
//<NavLink href={innerPages.germanlanguageforclass12.url}>for Class 12th</NavLink>
export const NavLinks = styled.li`
  ${tw`inline-block lg:mx-2`}
  @media only screen and (min-width:1024px) {
    &:hover .submenu {
      ${tw`block absolute`}
    }
  }
  > span {
    ${tw`text-lg my-2 lg:text-sm lg:mx-0 lg:my-0 flex justify-between
        font-semibold text-black tracking-wide transition duration-300
        pb-1 lg:border-b-2 border-transparent hover:border-brand-500 hocus:text-brand-500`}
  }
  .submenu {
    box-shadow: 0 0 10px 0 rgb(0 0 0 / 20%);
    width: 236px;
    margin-top: -3px;
    ${tw`bg-white lg:rounded lg:border-t-4 border-brand-500 lg:hidden`}
    a {
      ${tw`block py-2 px-4 mr-0 ml-0 border-b-0`}
    }
  }
  span {
    .caret {
      transform: rotate(90deg);
      display: inline-block;
      transition: 0.2s;
    }
    &:hover .caret {
      transition: 0.2s;
      transform: rotate(-90deg);
      path {
        fill: #e28302;
      }
    }
  }
`;

/* hocus: stands for "on hover or focus"
 * hocus:bg-primary-700 will apply the bg-primary-700 class on hover or focus
 */
export const NavLink = tw.a`
no-underline
  text-lg my-2 lg:text-sm lg:mx-0 lg:my-0
  font-semibold text-black tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-brand-500 hocus:text-brand-500
`;

export const PrimaryLink = tw(NavLink)`
  xs:mx-3
  lg:mx-0
  px-8 py-3 bg-[#3f6c68] text-gray-100
  hocus:bg-secondary-900 hocus:text-gray-200 focus:shadow-outline
  border-b-0
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};

  img {
    ${tw`mr-3`}
  }
`;

export const MobileNavLinksContainer = styled.nav`
  ${tw`flex flex-row items-center  justify-between gap-[240px] md:gap-[600px]`}
  ${LogoLink} {
    ${tw`z-20`}
  }
`;
export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-gray-500 transition duration-300
`;
export const MobileNavLinks = motion(styled.div`
  ${tw`lg:hidden z-10 fixed top-0 left-0 right-0 bottom-0 inset-x-0 mx-0 my-0 px-0 py-20 text-white bg-blackStep-200`}
  ${NavLinks} {
    ${tw`flex flex-col border-b-2 border-white border-opacity-5 px-0`}
    &:last-child {
      ${tw`border-b-0`}
    }
    > span,
    a {
      ${tw`text-white px-3`}
      path {
        fill: #ffffff;
      }
    }
    .submenu {
      transition: height ease 0.2s;
      ${tw`h-0 opacity-0 w-full hidden`}
      &.active {
        ${tw`h-auto opacity-100 block`}
      }
      a {
        ${tw`text-black`}
      }
      ${NavLink} {
        ${tw`text-sm`}
      }
    }
  }
`);

export const DesktopNavLinks = tw.nav`
  relative hidden lg:flex flex-1 justify-between items-center
`;

export default ({
  roundedHeaderButton = false,
  hideContactButton = false,
  logoLink,
  links,
  className,
  collapseBreakpointClass = "lg",
  innerPages = components.innerPages,
  blocks = components.blocks,
}) => {
  const [offset, setOffset] = useState(0);
  const [headerSticky, setHeader] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [tickerText, setTickerText] = useState("");

  useEffect(() => {
    window.onscroll = () => {
      if (offset > 0 || offset < window.pageYOffset)
        setOffset(window.pageYOffset);
    };
    getTickerText();
  }, []);

  // useEffect(() => {}, []);

  const getTickerText = async () => {
    const response = await fetch(
      "https://cavijenderaggarwal.com/public/content.txt"
    );
    const tickerText = await response.text();
    console.log(tickerText);
    setTickerText(tickerText);
  };

  const handleToggle = (index) => {
    //////console.log(index);
    if (isActive === index) {
      return setIsActive("0");
    }
    setIsActive(index);
  };

  const defaultLinks = [
    <NavBar key="main-nav" tw="px-0 md:px-0  py-10 md:py-20 lg:py-0 lg:px-30">
      <NavLinks>
        <NavLink href="/" tw="lg:ml-0! lg:mr-3">
          Home
        </NavLink>
      </NavLinks>
      <NavLinks>
        <NavLink href={innerPages.aboutus.url} tw="lg:ml-0! lg:mr-3">
          About
        </NavLink>
      </NavLinks>
      <NavLinks key={1} onClick={() => handleToggle(1)} tw="relative">
        <span>
          Products
          <CaretIcon tw="w-6 h-6" className="caret" />
        </span>

        <div className={isActive === 1 ? "submenu active" : "submenu"}>
          <NavLink href="https://cavijenderaggarwal.com/products/?data=CA/CMA Inter Books">
            CA / CMA Inter Books
          </NavLink>

          <NavLink href="https://cavijenderaggarwal.com/products/?data=CA Inter Lectures">
            CA Inter Lectures
          </NavLink>

          <NavLink href="https://cavijenderaggarwal.com/products/?data=CMA%20Inter%20Lectures">
            CMA Inter Lectures
          </NavLink>

          <NavLink href="https://cavijenderaggarwal.com/products/?data=CA%20%2F%20CMA%20Final%20Books">
            CA / CMA Final Books
          </NavLink>

          <NavLink href="https://cavijenderaggarwal.com/products/?data=CA%20Final%20Lectures">
            CA Final Lectures
          </NavLink>

          <NavLink href="https://cavijenderaggarwal.com/products/?data=CMA%20Final%20Lectures">
            CMA Final Lectures
          </NavLink>
        </div>
      </NavLinks>

      <NavLinks key={4} onClick={() => handleToggle(4)}>
        <span>
          Franchise
          <CaretIcon tw="w-6 h-6" className="caret" />
        </span>
        <div className={isActive === 4 ? "submenu active" : "submenu"}>
          <NavLink href="https://cavijenderaggarwal.com/franchise/index.php/franchise_register">
            Request a Franchise
          </NavLink>
          <NavLink href="https://cavijenderaggarwal.com/franchise/index.php/admin">
            Franchise Login
          </NavLink>
        </div>
      </NavLinks>
      <NavLinks key={5}>
        <NavLink href={innerPages.blog.url} tw="lg:ml-0! lg:mr-3">
          Blogs
        </NavLink>
      </NavLinks>
      <NavLinks key={6}>
        <NavLink href={innerPages.testimonial.url} tw="lg:ml-0! lg:mr-3">
          Testimonials
        </NavLink>
      </NavLinks>
      {/* <NavLinks key={7}>
        <NavLink href={innerPages.blog.url} tw="lg:ml-0! lg:mr-3">
          Mock Test Series
        </NavLink>
      </NavLinks> */}
      <NavLinks tw="lg:border-l-2 border-gray-300 lg:pl-6">
        <NavLink href={innerPages.login.url} tw="lg:ml-0! lg:mr-3">
          Login
        </NavLink>
        {!hideContactButton && (
          <PrimaryLink
            css={roundedHeaderButton && tw`rounded-full`}
            className="bt-round"
            href={innerPages.contactus.url}
          >
            Contact Us
          </PrimaryLink>
        )}
      </NavLinks>
    </NavBar>,
  ];

  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss =
    collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
    <LogoLink href="/">
      <img
        src="https://pushsales.b-cdn.net/VAC%20Logo.png"
        alt="logo"
        className="vac-logo"
      />
    </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <>
      <TopBar className="header-top-bar">
        <div className="main-top-bar">
          <div className="header-top flex justify-between items-center px-4 text-sm text-white">
            {/* Left: Marquee */}
            <div
              className="header-top-left"
              tw="flex-1 min-w-0 overflow-hidden"
            >
              <div
                className="header-notify"
                style={{ width: "100%", overflow: "hidden" }}
              >
                <marquee behavior="scroll" direction="left">
                  <b>Ongoing Batches:</b> {tickerText}
                </marquee>
              </div>
            </div>

            {/* Right Section */}
            <div
              className="header-top-right flex items-center gap-3"
              tw="flex-shrink-0 whitespace-nowrap"
            >
              {/* Login & Register */}

              <ul className="header-info flex items-center gap-3">
                {/* Phone & WhatsApp */}
                <li className="social-icon flex items-center gap-3">
                  <a
                    href="https://www.youtube.com/@TaxwithoutTears"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Youtube style={iconStyle} />
                  </a>

                  <a
                    href="https://www.facebook.com/vagarwal123"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Facebook style={iconStyle} />
                  </a>

                  <a
                    href="https://www.instagram.com/cavijenderaggarwal"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Instagram style={iconStyle} />
                  </a>

                  <a
                    href="https://www.linkedin.com/in/ca-vijender-aggarwal-01836733a/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Linkedin style={iconStyle} />
                  </a>
                </li>
              </ul>
              <div className="social-login">
                <a
                  href="/login"
                  className="text-white hover:underline font-semibold"
                >
                  Login
                </a>
                <a
                  href="/register"
                  className="bg-white text-black px-2 py-1 rounded text-xs font-semibold hover:bg-gray-200 transition"
                >
                  Register
                </a>
              </div>
            </div>
          </div>
        </div>
      </TopBar>

      <Header className={className || "header-light aurum-navbar"}>
        <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
          {logoLink}
          {links}
        </DesktopNavLinks>

        <MobileNavLinksContainer
          css={collapseBreakpointCss.mobileNavLinksContainer}
        >
          {logoLink}
          <MobileNavLinks
            initial={{ x: "150%", display: "none" }}
            animate={animation}
            css={collapseBreakpointCss.mobileNavLinks}
          >
            {links}
          </MobileNavLinks>
          <NavToggle
            onClick={toggleNavbar}
            className={showNavLinks ? "open" : "closed"}
          >
            {showNavLinks ? (
              <CloseIcon tw="w-6 h-6" />
            ) : (
              <MenuIcon tw="w-6 h-6" />
            )}
          </NavToggle>
        </MobileNavLinksContainer>
      </Header>
    </>
  );
};

/* The below code is for generating dynamic break points for navbar.
 * Using this you can specify if you want to switch
 * to the toggleable mobile navbar at "sm", "md" or "lg" or "xl" above using the collapseBreakpointClass prop
 * Its written like this because we are using macros and we can not insert dynamic variables in macros
 */

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`,
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`,
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
};
